import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';
import ProductCard from '../components/ProductCard';
import StructuredData from '../components/StructuredData';
import FaqAnswer from '../components/FaqAnswer';
import { getProductCategoryFaqs, toFaqPageJsonLd } from '../data/faqData';
import { Product, Category, Subcategory } from '../types';
import { getGoogleDriveDirectLink } from '../utils';
import styles from './styles/ProductsPage.module.css';
import { sortByCategoryOrder } from '../utils/categoryOrder';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const PRODUCTS_PER_PAGE = 24;

const getProductSearchPriority = (product: Product, rawTerm: string) => {
    const term = rawTerm.trim().toLowerCase();
    const name = product.NombresProductos.toLowerCase();
    if (name === term) return 0;
    if (name.startsWith(term)) return 1;
    if (name.includes(term)) return 2;
    if ((product.descripcion || '').toLowerCase().includes(term)) return 3;
    if ((product.descripcionProducto || '').toLowerCase().includes(term)) return 4;
    return 5;
};

const ProductsPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const [displayedProducts, setDisplayedProducts] = useState<Product[]>([]);
    const [totalProducts, setTotalProducts] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const [categories, setCategories] = useState<Category[]>([]);
    const [subcategories, setSubcategories] = useState<Subcategory[]>([]);

    const [loading, setLoading] = useState<boolean>(true);
    const [openCategoryId, setOpenCategoryId] = useState<number | null>(null);

    const DEFAULT_BANNER = 'https://acdn-us.mitiendanube.com/stores/002/088/259/themes/rio/2-img-2058235534-1748022167-c6230adf802867a20a85436bd632eb021748022168-1920-1920.webp?490711910907858905';

    const catParam = searchParams.get('cat') || '';
    const subParam = searchParams.get('sub') || '';
    const searchParam = searchParams.get('search') || '';
    const pageParam = Number(searchParams.get('page') || '1');
    const currentPage = Number.isFinite(pageParam) && pageParam > 0 ? Math.floor(pageParam) : 1;

    // Banner info
    const [bannerTitle, setBannerTitle] = useState('Todos los Productos');
    const [bannerImage, setBannerImage] = useState<string | null>(DEFAULT_BANNER);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [resCat, resSub] = await Promise.all([
                    fetch(`${API_URL}/menu`),
                    fetch(`${API_URL}/subcategorias`)
                ]);

                const categoriesData = await resCat.json();
                const subcategoriesData = await resSub.json();

                // Safety checks for non-array responses (e.g. backend error objects)
                const safeCategories = Array.isArray(categoriesData) ? categoriesData : [];
                const safeSubcategories = Array.isArray(subcategoriesData) ? subcategoriesData : [];

                // Deduplicate categories just in case
                const uniqueCategories = safeCategories.filter((cat: Category, index: number, self: Category[]) =>
                    index === self.findIndex((t) => (
                        t.idCategoria === cat.idCategoria
                    ))
                );

                setCategories(sortByCategoryOrder(uniqueCategories));
                setSubcategories(safeSubcategories);
            } catch (error) {
                console.error(error);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        const fetchProducts = async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams({
                    page: String(currentPage),
                    per_page: String(PRODUCTS_PER_PAGE)
                });
                if (subParam) params.set('sub', subParam);
                else if (catParam) params.set('cat', catParam);
                if (searchParam) params.set('search', searchParam);

                const res = await fetch(`${API_URL}/productos?${params.toString()}`);
                const data = await res.json();

                if (Array.isArray(data)) {
                    let fallbackItems = data;
                    if (searchParam) {
                        const term = searchParam.toLowerCase();
                        fallbackItems = fallbackItems.filter((p: Product) =>
                            p.NombresProductos.toLowerCase().includes(term) ||
                            (p.descripcion && p.descripcion.toLowerCase().includes(term)) ||
                            (p.descripcionProducto && p.descripcionProducto.toLowerCase().includes(term)) ||
                            (p.tags && p.tags.toLowerCase().includes(term)) ||
                            (p.marca && p.marca.toLowerCase().includes(term)) ||
                            (p.nombre_subcategoria && p.nombre_subcategoria.toLowerCase().includes(term)) ||
                            (p.nombre_categoria && p.nombre_categoria.toLowerCase().includes(term))
                        ).sort((a: Product, b: Product) =>
                            getProductSearchPriority(a, searchParam) - getProductSearchPriority(b, searchParam) ||
                            a.NombresProductos.localeCompare(b.NombresProductos)
                        );
                    } else if (subParam) {
                        fallbackItems = fallbackItems.filter((p: Product) => p.nombre_subcategoria === subParam);
                    } else if (catParam) {
                        fallbackItems = fallbackItems.filter((p: Product) => p.nombre_categoria === catParam);
                    }
                    setDisplayedProducts(fallbackItems);
                    setTotalProducts(fallbackItems.length);
                    setTotalPages(1);
                    return;
                }

                const items = Array.isArray(data?.items) ? data.items : [];
                setDisplayedProducts(items);
                setTotalProducts(Number(data?.total || 0));
                setTotalPages(Math.max(Number(data?.total_pages || 1), 1));
            } catch (error) {
                console.error(error);
                setDisplayedProducts([]);
                setTotalProducts(0);
                setTotalPages(1);
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, [catParam, subParam, searchParam, currentPage]);

    // Efecto para actualizar el banner cuando cambian los datos o la URL
    useEffect(() => {
        let title = 'Todos los Productos';
        let img = DEFAULT_BANNER; // Por defecto el banner del home

        if (searchParam) {
            // Filtrar por búsqueda (nombre o descripción)
            title = `Resultados para: "${searchParam}"`;

        } else if (subParam) {
            // Filtrar por subcategoría
            title = subParam;
            const subVals = subcategories.find(s => s.nombre === subParam);
            // Si tiene imagen específica, la usamos, sino queda la default
            if (subVals?.imagen_url) img = getGoogleDriveDirectLink(subVals.imagen_url) || subVals.imagen_url;

        } else if (catParam) {
            // Filtrar por categoría
            title = catParam;
            const catVals = categories.find(c => c.nombre === catParam);
            // Si tiene imagen específica, la usamos, sino queda la default
            if (catVals?.imagen_url) img = getGoogleDriveDirectLink(catVals.imagen_url) || catVals.imagen_url;
        }

        setBannerTitle(title);
        setBannerImage(img);

    }, [catParam, subParam, searchParam, categories, subcategories]);

    useEffect(() => {
        if (!catParam && !subParam) {
            setOpenCategoryId(null);
            return;
        }

        const matchingCategory = categories.find(cat =>
            cat.nombre === catParam ||
            cat.subcategorias.some(sub => sub.nombre === subParam)
        );

        if (matchingCategory) {
            setOpenCategoryId(matchingCategory.idCategoria);
        }
    }, [categories, catParam, subParam]);

    // Función para manejar el clic en el sidebar (filtro por categoría)
    const handleCategoryClick = (categoryName: string | null) => {
        if (categoryName) {
            setSearchParams({ cat: categoryName });
        } else {
            setOpenCategoryId(null);
            setSearchParams({});
        }
    };

    const handleSubcategoryClick = (categoryName: string, subcategoryName: string) => {
        setSearchParams({ cat: categoryName, sub: subcategoryName });
    };

    const toggleCategory = (category: Category) => {
        if (openCategoryId === category.idCategoria) {
            handleCategoryClick(null);
            return;
        }

        setOpenCategoryId(category.idCategoria);
        handleCategoryClick(category.nombre);
    };

    const handlePageChange = (page: number) => {
        const nextPage = Math.min(Math.max(page, 1), totalPages);
        const nextParams = new URLSearchParams(searchParams);
        if (nextPage <= 1) {
            nextParams.delete('page');
        } else {
            nextParams.set('page', String(nextPage));
        }
        setSearchParams(nextParams);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const paginationPages = Array.from(
        { length: totalPages },
        (_, index) => index + 1
    ).filter(page =>
        page === 1 ||
        page === totalPages ||
        Math.abs(page - currentPage) <= 1
    );

    const productFaqs = useMemo(
        () => getProductCategoryFaqs(catParam, subParam),
        [catParam, subParam]
    );

    const faqTitle = subParam
        ? `Preguntas frecuentes sobre ${subParam}`
        : catParam
            ? `Preguntas frecuentes sobre ${catParam}`
            : 'Preguntas frecuentes sobre nuestros productos';

    return (
        <div className={styles['catalog-container']}>
            <StructuredData data={toFaqPageJsonLd(productFaqs)} />
            {/* Sidebar Dinámico */}
            <aside className={styles['filters-sidebar']}>
                <h3>Categorías</h3>
                <ul className={styles['category-tree']}>
                    <li>
                        <button
                            type="button"
                            className={`${styles['filter-reset']} ${!searchParams.get('cat') && !searchParams.get('sub') ? styles['active-filter'] : ''}`}
                            onClick={() => handleCategoryClick(null)}
                        >
                            Todos los productos
                        </button>
                    </li>
                    {categories.map(cat => {
                        const isOpen = openCategoryId === cat.idCategoria;
                        const isActiveCategory = searchParams.get('cat') === cat.nombre;
                        const activeSub = searchParams.get('sub');

                        return (
                            <li key={cat.idCategoria} className={styles['category-filter-item']}>
                                <button
                                    type="button"
                                    className={`${styles['category-toggle']} ${isOpen ? styles['category-open'] : ''} ${isActiveCategory ? styles['active-filter'] : ''}`}
                                    onClick={() => toggleCategory(cat)}
                                    aria-expanded={isOpen}
                                >
                                    <span>{cat.nombre}</span>
                                    {cat.subcategorias.length > 0 && (
                                        isOpen ? <FaChevronDown className={styles['filter-chevron']} /> : <FaChevronRight className={styles['filter-chevron']} />
                                    )}
                                </button>
                                {isOpen && cat.subcategorias.length > 0 && (
                                    <ul className={styles['subcategory-filter-list']}>
                                        {cat.subcategorias.map(sub => (
                                            <li key={sub.idSubcategoria}>
                                                <button
                                                    type="button"
                                                    className={`${styles['subcategory-filter-item']} ${activeSub === sub.nombre ? styles['subcategory-active'] : ''}`}
                                                    onClick={() => handleSubcategoryClick(cat.nombre, sub.nombre)}
                                                >
                                                    {sub.nombre}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </aside>

            <main className={styles['catalog-content']}>
                {/* BANNER DE CABECERA (Si existe imagen) */}
                {bannerImage && (
                    <div className={styles['catalog-banner']} style={{ backgroundImage: `url(${bannerImage})` }}>
                        <div className={styles['banner-overlay']}>
                            <h1>{bannerTitle}</h1>
                        </div>
                    </div>
                )}

                {/* Header simple si no hay banner image, o como complemento */}
                {!bannerImage && (
                    <header className={styles['catalog-header']}>
                        <h1>{bannerTitle}</h1>
                        <span>{totalProducts} resultados</span>
                    </header>
                )}

                {/* Mostrar conteo si hay banner */}
                {bannerImage && (
                    <div className={styles['results-count']}>
                        <span>{totalProducts} productos encontrados</span>
                    </div>
                )}

                {loading ? (
                    <div className={styles.loader}>Cargando...</div>
                ) : (
                    <>
                        {displayedProducts.length === 0 ? (
                            <div className={styles['empty-products']}>No hay productos para mostrar.</div>
                        ) : (
                            <div className={styles['products-grid']}>
                                {displayedProducts.map(prod => (
                                    <Link
                                        key={prod.idProductos}
                                        to={`/producto/${prod.idProductos}`}
                                        style={{ textDecoration: 'none', color: 'inherit' }}
                                    >
                                        <ProductCard product={prod} />
                                    </Link>
                                ))}
                            </div>
                        )}
                        {totalPages > 1 && (
                            <nav className={styles.pagination} aria-label="Paginacion de productos">
                                <button type="button" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage <= 1}>
                                    Anterior
                                </button>
                                {paginationPages.map((page, index) => {
                                    const previousPage = paginationPages[index - 1];
                                    const showGap = previousPage && page - previousPage > 1;
                                    return (
                                        <React.Fragment key={page}>
                                            {showGap && <span className={styles['pagination-gap']}>...</span>}
                                            <button
                                                type="button"
                                                onClick={() => handlePageChange(page)}
                                                className={page === currentPage ? styles['pagination-active'] : ''}
                                                aria-current={page === currentPage ? 'page' : undefined}
                                            >
                                                {page}
                                            </button>
                                        </React.Fragment>
                                    );
                                })}
                                <button type="button" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages}>
                                    Siguiente
                                </button>
                            </nav>
                        )}
                        <section className={styles['catalog-faq']} aria-labelledby="catalog-faq-title">
                            <div className={styles['catalog-faq-header']}>
                                <span>FAQ por categoria</span>
                                <h2 id="catalog-faq-title">{faqTitle}</h2>
                            </div>
                            <div className={styles['catalog-faq-list']}>
                                {productFaqs.map((faq) => (
                                    <article className={styles['catalog-faq-item']} key={faq.question}>
                                        <h3>{faq.question}</h3>
                                        <FaqAnswer answer={faq.answer} className={styles['catalog-faq-answer']} />
                                    </article>
                                ))}
                            </div>
                        </section>
                    </>
                )}
            </main>
        </div>
    );
};

export default ProductsPage;
